import React from "react";
import {
  Shield,
  GraduationCap,
  Calendar,
  MapPin,
  MessageSquare,
  ChevronRight,
  Search,
} from "lucide-react";
import { Senior, UserPreferences } from "../../types";

interface RecommendationPageProps {
  seniors: Senior[];
  maskName: (name: string) => string;
  setSelectedSenior: (senior: Senior) => void;
  setCurrentPage: (page: string) => void;
  userPreferences: UserPreferences;
  setUserPreferences: (preferences: UserPreferences) => void;
  showResults: boolean;
  setShowResults: (show: boolean) => void;
}

const RecommendationPage: React.FC<RecommendationPageProps> = ({
  seniors,
  maskName,
  setSelectedSenior,
  setCurrentPage,
  userPreferences,
  setUserPreferences,
  showResults,
  setShowResults,
}) => {

  // 희망 조건 입력 화면
  if (!showResults) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">선배 추천</h2>
        <p className="text-gray-600 mb-6">
          희망 조건을 입력하면 맞춤 선배를 추천해드립니다
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              희망 근무지역
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg"
              value={userPreferences.location}
              onChange={(e) =>
                setUserPreferences({
                  ...userPreferences,
                  location: e.target.value,
                })
              }
            >
              <option value="">전체</option>
              <option value="전주시">전주시</option>
              <option value="군산시">군산시</option>
              <option value="익산시">익산시</option>
              <option value="정읍시">정읍시</option>
              <option value="남원시">남원시</option>
              <option value="김제시">김제시</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              희망 직군
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg"
              value={userPreferences.jobField}
              onChange={(e) =>
                setUserPreferences({
                  ...userPreferences,
                  jobField: e.target.value,
                })
              }
            >
              <option value="">전체</option>
              <option value="IT/소프트웨어">IT/소프트웨어</option>
              <option value="금융">금융</option>
              <option value="공공행정">공공행정</option>
              <option value="바이오">바이오</option>
              <option value="제조업">제조업</option>
              <option value="의료/보건">의료/보건</option>
              <option value="건설/엔지니어링">건설/엔지니어링</option>
              <option value="유통/서비스">유통/서비스</option>
              <option value="교육">교육</option>
              <option value="사회복지">사회복지</option>
              <option value="기타">기타</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              희망 연봉 (선택사항)
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg"
              value={userPreferences.salary}
              onChange={(e) =>
                setUserPreferences({
                  ...userPreferences,
                  salary: e.target.value,
                })
              }
            >
              <option value="">상관없음</option>
              <option value="3000">3,000만원 이상</option>
              <option value="3500">3,500만원 이상</option>
              <option value="4000">4,000만원 이상</option>
              <option value="4500">4,500만원 이상</option>
              <option value="5000">5,000만원 이상</option>
            </select>
          </div>
        </div>

        <button
          onClick={() => setShowResults(true)}
          className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
        >
          <Search className="w-5 h-5 mr-2" />
          선배 찾기
        </button>
      </div>
    );
  }

  // 필터링된 선배 목록 표시
  const filteredSeniors = seniors.filter((senior) => {
    return (
      (!userPreferences.location || senior.location === userPreferences.location) &&
      (!userPreferences.jobField || senior.jobField === userPreferences.jobField)
    );
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">추천 선배</h2>
        <button
          onClick={() => setShowResults(false)}
          className="text-blue-600 text-sm hover:underline"
        >
          다시 검색
        </button>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-xl mb-4">
        <p className="text-sm text-blue-800">
          <strong>검색 조건:</strong> 
          {userPreferences.location && ` ${userPreferences.location}`}
          {userPreferences.jobField && ` · ${userPreferences.jobField}`}
          {(!userPreferences.location && !userPreferences.jobField) && " 전체"}
        </p>
        <p className="text-xs text-blue-600 mt-1">
          총 {filteredSeniors.length}명의 선배를 찾았습니다
        </p>
      </div>

      {filteredSeniors.length === 0 ? (
        <div className="bg-gray-50 p-8 rounded-xl text-center">
          <p className="text-gray-600 mb-2">조건에 맞는 선배를 찾을 수 없습니다</p>
          <p className="text-sm text-gray-500 mb-4">
            다른 조건으로 다시 검색해보세요
          </p>
          <button
            onClick={() => setShowResults(false)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
          >
            다시 검색하기
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSeniors.map((senior) => (
        <div
          key={senior.id}
          className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-semibold text-lg flex items-center">
                {maskName(senior.name)}
                <Shield className="w-4 h-4 ml-1 text-gray-400" />
              </h3>
              <p className="text-gray-600">
                {senior.company} · {senior.position}
              </p>
            </div>
            <div className="text-right">
              <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {senior.matchRate}% 매칭
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-sm text-gray-500 mb-3">
            <span className="flex items-center">
              <GraduationCap className="w-4 h-4 mr-1" />
              {senior.university}
            </span>
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {senior.year}
            </span>
            <span className="flex items-center">
              <MapPin className="w-4 h-4 mr-1" />
              {senior.location}
            </span>
          </div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex gap-2">
              {senior.badges.map((badge, idx) => (
                <span
                  key={idx}
                  className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs"
                >
                  🏆 {badge}
                </span>
              ))}
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <MessageSquare className="w-3 h-3 mr-1" />
              답변 {senior.responses}개 · 도움 {senior.helpfulRate}%
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedSenior(senior);
              setCurrentPage("profile");
            }}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            프로필 보기 <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      ))}
    </div>
  )}
</div>
);
};

export default RecommendationPage;